import { PropsWithChildren, useState } from "react";
import { FieldArrayPath, FieldArrayWithId, FieldValues } from "react-hook-form";
import styled from "styled-components";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FieldError } from "../../BaseField";
import { ArrayControl } from "../../_common/ArrayControl";
import { RenderType } from "../FieldsArray";

const MainRow = styled.div`
  align-items: flex-end;
  width: 100%;
  min-height: 36px;
  position: relative;
  align-items: flex-start;
`;

const Item = styled.div<{ isShow?: boolean }>`
  transition: all 0.6s ease-out;
  overflow: hidden;

  animation: showField 0.2s ease-out;

  .field {
    flex-grow: 1;
    margin-bottom: 0;
  }

  @keyframes showField {
    0% {
      opacity: 0;
      max-height: 0px;
    }
    100% {
      opacity: 1;
      max-height: 200px;
    }
  }
`;

export interface FieldsArrayRowProps<
  TFieldValues extends FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues>,
> {
  field: FieldArrayWithId<TFieldValues, TFieldArrayName>;
  index: number;
  hideDefaultControl?: boolean;
  renderRow: RenderType<TFieldValues, TFieldArrayName>;
  rowError: any;
  defaultErrorMessage?: string;
  fieldsLength: number;
  readOnly?: boolean;
  initialIndexAllowedToEdit?: number;
  remove: (index: number) => void;
  name: string;
}

export function FieldsArrayRow<
  TFieldValues extends FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues>,
>({
  field,
  index,
  renderRow,
  rowError,
  fieldsLength,
  readOnly,
  remove,
  initialIndexAllowedToEdit,
  defaultErrorMessage,
  hideDefaultControl,
  name,
}: PropsWithChildren<FieldsArrayRowProps<TFieldValues, TFieldArrayName>>) {
  const [isShowAnimation, setIsShowAnimation] = useState(true);

  const onRemove = (index: number) => {
    setIsShowAnimation(false);
    new Promise((resolve) => setTimeout(resolve, 1000)).finally(() =>
      remove(index),
    );
  };

  const arrayControl = (
    <ArrayControl
      remove={(index) => onRemove(index)}
      fieldsLength={fieldsLength}
      index={index}
      initialIndexAllowedToEdit={initialIndexAllowedToEdit}
      readOnly={readOnly}
    />
  );

  return (
    <Item
      style={{
        maxHeight: isShowAnimation ? "1000px" : "0px",
        opacity: isShowAnimation ? 1 : 0,
      }}
      key={field.id}
    >
      {/* item line and remove icon  */}
      <MainRow>
        {renderRow(`${name}[${index}]`, field, index)}
        {!hideDefaultControl ? arrayControl : null}
      </MainRow>

      {/* group error */}
      {rowError && (
        <FieldError className="field-error">
          <FontAwesomeIcon icon={faExclamationCircle} size="1x" />
          {rowError?.message || defaultErrorMessage}
        </FieldError>
      )}
    </Item>
  );
}

FieldsArrayRow.defaultProps = {
  defaultValue: [],
  initialIndexAllowedToEdit: 0,
};
